import { Component, OnInit, OnDestroy, Input, ViewChild, HostBinding } from '@angular/core';
import { from, Subscription } from 'rxjs';
import { InfiniteScrollCustomEvent, IonInfiniteScroll, Platform } from '@ionic/angular';
import { ApiRequestService } from 'netsocial-lib/src/app/services/api-request/api-request.service';
import { observable } from 'mobx-angular';
import { PaginatedItems } from 'netsocial-lib/src/app/interfaces/paginated-items';
import { formatForApi } from 'netsocial-lib/src/app/utils/date-utils';
import { ErrorService } from 'netsocial-posting/src/app/services/error/error.service';
import {
  LeaderboardService,
  GetLeaderboardParams,
} from '../../../services/leaderboard/leaderboard.service';
import { LeaderboardEntry } from '../../../interfaces/leaderboard-entry';

@Component({
  selector: 'leaderboard-list',
  templateUrl: './leaderboard-list.component.html',
  styleUrls: ['./leaderboard-list.component.scss'],
})
export class LeaderboardListComponent implements OnInit, OnDestroy {
  @ViewChild(IonInfiniteScroll) infiniteScroll: IonInfiniteScroll;

  @observable
  @Input()
  paginatedLeaderboard?: PaginatedItems<LeaderboardEntry>;

  @Input() readonly: boolean;
  @Input() loading: boolean;
  @Input() showFullButton = true;

  @HostBinding('class.full-view')
  @Input()
  fullView: boolean;

  loadingMore: boolean;
  isDesktop: boolean;
  leaderboard = [];

  show = 4;
  showOptions = [10, 100, 500];

  @observable @Input() period: GetLeaderboardParams['period'] = 7;
  periodOptions: Array<GetLeaderboardParams['period']> = [7, 30, 60, 90];

  @observable @Input() endDate: Date;
  @observable @Input() startDate: Date;

  private contextOwnerSubscription?: Subscription;
  private getLeaderboardSubscription?: Subscription;

  constructor(
    private leaderboardService: LeaderboardService,
    private platform: Platform,
    private errorService: ErrorService,
    private apiRequest: ApiRequestService
  ) {}

  ngOnInit(): void {
    this.isDesktop = this.platform.is('desktop');

    if (!this.readonly) {
      this.contextOwnerSubscription = this.apiRequest.getContextOwner().subscribe(() => {
        this.getLeaderboard();
      });
    }
  }

  ngOnDestroy(): void {
    this.contextOwnerSubscription?.unsubscribe();
    this.getLeaderboardSubscription?.unsubscribe();
  }

  async getLeaderboard(page = 0): Promise<void> {
    if (page === 0) {
      this.paginatedLeaderboard = undefined;
    }
    const loadingFlag = !page ? 'loading' : 'loadingMore';
    this[loadingFlag] = true;
    const leaderboardData: GetLeaderboardParams = {
      perPage: this.show,
      first: page,
    };

    if (this.startDate && this.endDate) {
      leaderboardData['startDate'] = formatForApi(this.startDate);
      leaderboardData['endDate'] = formatForApi(this.endDate);
    } else {
      leaderboardData['period'] = this.period;
    }

    this.getLeaderboardSubscription = from(
      this.errorService.resolve(this.leaderboardService.getLeaderboard(leaderboardData), {
        errorMessage: 'components.leaderboardList.errors.getLeaderboard',
      })
    ).subscribe((results) => {
      if (this.paginatedLeaderboard && results) {
        this.paginatedLeaderboard.loadNextPage(results.page);
      } else if (results) {
        this.paginatedLeaderboard = results.page;
      }
    });

    this[loadingFlag] = false;
  }

  async reachedInfinity(event: InfiniteScrollCustomEvent): Promise<void> {
    await this.getLeaderboard(this.paginatedLeaderboard?.paging.next);

    event.target.complete();

    if (this.infiniteScroll && !this.paginatedLeaderboard?.canLoadMore) {
      this.infiniteScroll.disabled = true;
    }
  }
}
